import { useNavigate } from "react-router-dom";
import { useFormikContext, Field, FieldArray } from "formik";
import { TextField } from "formik-material-ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";

import Tooltip from "../../themes/components/Tooltip";
import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";

import InputLabel from "@mui/material/InputLabel";
import { COLORS } from "../../themes/Base";
import AddIcon from "@mui/icons-material/Add";
import CustomCheckbox from "../../components/Form/CustomCheckbox";
import DeleteIcon from "../../components/Icons/DeleteIcon";
import CheckIndicator, { CheckIndicatorStatus } from "../../components/Icons/CheckIndicator";

const SupplierForm: React.FC = () => {
  const navigate = useNavigate();
  const { values, setFieldValue, handleSubmit } = useFormikContext<SupplierCreateVars>();

  const { classes } = useSharedStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();
  const isEditMode = Boolean(values?.id);

  const handleFieldRequiredClick = (fieldIdx: number) => {
    setFieldValue(`input.fields.${fieldIdx}.required`, !values.input.fields[fieldIdx].required);
  };

  const getEndpointValidationState = (url: string): CheckIndicatorStatus => {
    if (!url || url.trim() === "") return "empty";
    const matches = url.match(/\{\{(.*?)\}\}/g);
    if (!matches) return "valid";
    const fieldNames = values.input.fields.filter(f => !f.deleted).map(f => f.name);
    const allValid = matches.every((match) => {
      const name = match.replace("{{", "").replace("}}", "");
      return fieldNames.includes(name);
    });
    return allValid ? "valid" : "invalid";
  };

  const renderUrlWithInvalidsHighlighted = (url: string): React.ReactNode[] | string => {
    if (!url || url.trim() === "") return url;
    const fields = values.input.fields;
    const matches = url.match(/\{\{(.*?)\}\}/g);
    if (!matches) return url;
    const fieldNames = fields.filter(f => !f.deleted).map(f => f.name);
    const invalids = matches.filter((match) => {
      const name = match.replace("{{", "").replace("}}", "");
      return !fieldNames.includes(name);
    });
    if (!invalids.length) return url;
    const parts = url.split(new RegExp(`(${invalids.join('|')})`, 'g'));
    return parts.map((part, idx) =>
      invalids.includes(part) ? (
        <span key={idx} style={{ color: COLORS.error }}>{part}</span>
      ) : (
        <span key={idx}>{part}</span>
      )
    );
  };

  const endpointStates = {
    completed: getEndpointValidationState(values.input.endpoints.completed),
    quota_full: getEndpointValidationState(values.input.endpoints.quota_full),
    screened_out: getEndpointValidationState(values.input.endpoints.screened_out),
    terminated: getEndpointValidationState(values.input.endpoints.terminated),
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        mt: 12,
      }}
    >
      <Box
        className={classes.contentBox}
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: "80px",
        }}
      >
        <Typography variant="h2">Supplier Details</Typography>
        <Box className={classes.fieldBox}>
          <InputLabel>Supplier Name</InputLabel>
          <Field
            component={TextField}
            name="input.name"
            placeholder="Supplier Name"
            variant="outlined"
            fullWidth
            autoFocus
          />
        </Box>
        <Typography variant="h2">Endpoints</Typography>
        <Typography variant="subtitle1">
          Provide supplier endpoints for each response status type.
          <br />
          For any dynamic URL variable values use{" "}
          <code>
            {"{{"} {"}}"}
          </code>{" "}
          for starting and ending tags.
          <br />
          Between the <code>{"{{ }}"}</code> put the <strong>field name</strong> which value you want
          to pass, for example:
          <br />
          if you define field with name <code>lucid_rid</code> and value <code>[%RID%]</code> then
          your "Complete" endpoint may look like this:
          <br />
          <code>{"https://notch.insights.supply/cb?token=XYZ&RID={{lucid_rid}}"}</code>
          <br />
          You will see the field name highlighted in green if it exists in the fields list. If it highlights in red, it means the field does not exist.
        </Typography>
        <div className={classes.borderedBox}>
          <Typography variant="subtitle1" sx={{ p: 0 }}>Complete</Typography>
          <Typography variant="body1" sx={{ mb: 0, fontSize: 14, color: COLORS.textMain }}>URL</Typography>
          <Box className={classes.fieldBox} sx={{ display: "flex", gap: 2 }} alignItems={"center"}>
            <Field
              component={TextField}
              name="input.endpoints.completed"
              placeholder="Complete"
              variant="outlined"
              fullWidth
              error={endpointStates.completed === "invalid"}
            />
            <CheckIndicator status={endpointStates.completed} />
          </Box>
          {endpointStates.completed === "invalid" && (
            <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 2 }}>
              There is an invalid parameter:&nbsp;
              {renderUrlWithInvalidsHighlighted(values.input.endpoints.completed)}
            </Typography>
          )}
        </div>
        <div className={classes.borderedBox}>
          <Typography variant="subtitle1" sx={{ p: 0 }}>Quota Full</Typography>
          <Typography variant="body1" sx={{ mb: 0, fontSize: 14, color: COLORS.textMain }}>URL</Typography>
          <Box className={classes.fieldBox} sx={{ display: "flex", gap: 2 }}>
            <Field
              component={TextField}
              name="input.endpoints.quota_full"
              placeholder="Quota Full"
              variant="outlined"
              fullWidth
              error={endpointStates.quota_full === "invalid"}
            />
            <CheckIndicator status={endpointStates.quota_full} />
          </Box>
          {endpointStates.quota_full === "invalid" && (
            <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 2 }}>
              There is an invalid parameter:&nbsp;
              {renderUrlWithInvalidsHighlighted(values.input.endpoints.quota_full)}
            </Typography>
          )}
        </div>
        <div className={classes.borderedBox}>
          <Typography variant="subtitle1" sx={{ p: 0 }}>Screened Out</Typography>
          <Typography variant="body1" sx={{ mb: 0, fontSize: 14, color: COLORS.textMain }}>URL</Typography>
          <Box className={classes.fieldBox} sx={{ display: "flex", gap: 2 }}>
            <Field
              component={TextField}
              name="input.endpoints.screened_out"
              placeholder="Screened Out"
              variant="outlined"
              fullWidth
              error={endpointStates.screened_out === "invalid"}
            />
            <CheckIndicator status={endpointStates.screened_out} />
          </Box>
          {endpointStates.screened_out === "invalid" && (
            <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 2 }}>
              There is an invalid parameter:&nbsp;
              {renderUrlWithInvalidsHighlighted(values.input.endpoints.screened_out)}
            </Typography>
          )}
        </div>
        <div className={classes.borderedBox}>
          <Typography variant="subtitle1" sx={{ p: 0 }}>Quality Termination</Typography>
          <Typography variant="body1" sx={{ mb: 0, fontSize: 14, color: COLORS.textMain }}>URL</Typography>
          <Box className={classes.fieldBox} sx={{ display: "flex", gap: 2 }}>
            <Field
              component={TextField}
              name="input.endpoints.terminated"
              placeholder="Quality Termination"
              variant="outlined"
              fullWidth
              error={endpointStates.terminated === "invalid"}
            />
            <CheckIndicator status={endpointStates.terminated} />
          </Box>
          {endpointStates.terminated === "invalid" && (
            <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 2 }}>
              There is an invalid parameter:&nbsp;
              {renderUrlWithInvalidsHighlighted(values.input.endpoints.terminated)}
            </Typography>
          )}
        </div>

        <Box>
          {Object.values(endpointStates).includes("invalid") && (
            <Typography variant="subtitle2" sx={{ color: COLORS.error, mt: 2, mb: 1, py: 2 }}>
              One or more endpoints contain invalid fields. Please check that all field names match exactly with the ones defined below.
            </Typography>
          )}
        </Box>

        <FieldArray
          name="input.fields"
          render={(arrayHelpers) => (
            <>
              <Typography variant="h3">Fields</Typography>
              {values.input.fields.map((field, fIdx) =>
                field.deleted ? (
                  <div key={fIdx}></div>
                ) : (
                  <Box
                    key={fIdx}
                    className={classes.borderedBox}
                    display="flex"
                    alignItems="center"
                    justifyContent={"space-between"}
                    gap={2}
                    mb={2}
                  >
                    <Box className={classes.fieldBox} flex={1}>
                      <InputLabel>Field Name</InputLabel>
                      <Field
                        component={TextField}
                        name={`input.fields.${fIdx}.name`}
                        placeholder="Field Name"
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                    <Box className={classes.fieldBox} flex={1}>
                      <InputLabel>Field Value</InputLabel>
                      <Field
                        component={TextField}
                        name={`input.fields.${fIdx}.value`}
                        placeholder="Field Value"
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 120, justifyContent: "flex-end", mt: 1 }}>
                      <FormControlLabel
                        label="Required"
                        control={
                          <CustomCheckbox color="secondary" checked={values.input.fields[fIdx].required} />
                        }
                        onChange={() => handleFieldRequiredClick(fIdx)}
                      />
                      <Tooltip title="Delete" placement="top">
                        <IconButton
                          onClick={() => {
                            if (!field.id) {
                              arrayHelpers.remove(fIdx);
                            } else {
                              setFieldValue(`input.fields.${fIdx}.deleted`, true);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )
              )}
              <Button
                className={cx(buttonClasses.iconButton, buttonClasses.blue)}
                onClick={() => arrayHelpers.push({ name: "", value: "", required: false })}
              >
                <AddIcon /> Add Field
              </Button>
            </>
          )}
        />
      </Box>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#fff",
          borderTop: "1px solid #E4E8D3",
          display: "flex",
          padding: "16px 32px",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        <Button
          onClick={() => navigate("/suppliers")}
          variant="outlined"
          className={cx(buttonClasses.grey, buttonClasses.iconButton)}
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (
              Object.values(endpointStates).some(
                (state) => state === "invalid" || state === "empty"
              )
            ) {
              return;
            }
            handleSubmit();
          }}
          className={cx(buttonClasses.blue, buttonClasses.iconButton)}
        >
          {isEditMode ? "Save changes" : "Create supplier"}
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierForm;
